import React, { useState, useEffect } from 'react'
import { useNavigate, NavigateFunction } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import styles from './HomeActions.module.scss'
import ActionTabs from './ActionTabs.tsx'
import ActionsGrid from './ActionsGrid.tsx'
import { actionHandlers } from './actionHandlers.ts'
import { openFeedTextModal } from '../../../store/actions'
import type { ActionTab } from './ActionTabs.tsx'
import {
    FileText,
    DollarSign,
    Users,
    Building,
    Package,
    Receipt,
    UserPlus,
    ScrollText,
    Store,
    Calculator,
    ClipboardList,
    MonitorCog,
    FilePlus,
    FolderOpen,
    FolderKanban,
    Layers,
    ShoppingBag,
    LockKeyhole
} from 'lucide-react'

export interface Action {
    id: string
    title: string
    subtitle: string
    icon: React.ReactNode
    tab: ActionTab
    color: string
    onClick: () => void
}

interface HomeActionsProps {
    userData?: any
    openFeedTextModal: (config: { text: boolean }) => void
    tutorialActive?: boolean
}

const HomeActions: React.FC<HomeActionsProps> = ({ userData, openFeedTextModal, tutorialActive }) => {
    const [selectedTab, setSelectedTab] = useState<ActionTab>('obras')
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const isUserAdmin = userData?.rols && userData.rols.filter((rol: any) => rol.name === "ADMIN").length > 0

    useEffect(() => {
        const handleTutorialTabChange = (event: CustomEvent<{ tab: ActionTab }>) => {
            setSelectedTab(event.detail.tab)
        }

        window.addEventListener('tutorial-change-tab', handleTutorialTabChange as EventListener)

        return () => {
            window.removeEventListener('tutorial-change-tab', handleTutorialTabChange as EventListener)
        }
    }, [])

    const createActionHandler = (actionId: string, handler: (navigate: NavigateFunction, userId?: string, openFeedTextModal?: (config: { text: boolean }) => void, tab?: string, position?: number, dispatch?: any) => void, useUserData?: boolean) => {
        return () => {
            const allActions = isUserAdmin ? [...baseActions, adminAction] : baseActions
            const filtered = allActions.filter(action => action.tab === selectedTab)
            const position = filtered.findIndex(action => action.id === actionId) + 1
            if (useUserData) {
                handler(navigate, userData, openFeedTextModal, selectedTab, position, dispatch)
            } else {
                handler(navigate, userData?.id, openFeedTextModal, selectedTab, position, dispatch)
            }
        }
    }

    const baseActions: Action[] = [
        {
            id: 'presupuestos-obra',
            title: 'Presupuestos para mi obra',
            subtitle: 'Solicita presupuestos de profesionales',
            icon: <FileText size={24} />,
            tab: 'obras',
            color: '#234ba2',
            onClick: createActionHandler('presupuestos-obra', actionHandlers['presupuestos-obra'])
        },
        {
            id: 'cotizar-obras',
            title: 'Cotizar en obras',
            subtitle: 'Encuentra obras para cotizar',
            icon: <Receipt size={24} />,
            tab: 'obras',
            color: '#234ba2',
            onClick: createActionHandler('cotizar-obras', actionHandlers['cotizar-obras'])
        },
        {
            id: 'obras-publicas',
            title: 'Obras públicas',
            subtitle: 'Explora licitaciones públicas',
            icon: <Building size={24} />,
            tab: 'obras',
            color: '#f5b048',
            onClick: createActionHandler('obras-publicas', actionHandlers['obras-publicas'])
        },
        {
            id: 'mis-proyectos',
            title: 'Mis proyectos en Edify',
            subtitle: 'Gestiona tus proyectos publicados',
            icon: <FolderOpen size={24} />,
            tab: 'obras',
            color: '#234ba2',
            onClick: createActionHandler('mis-proyectos', actionHandlers['mis-proyectos'], true)
        },
        {
            id: 'estimacion-costos',
            title: 'Estimación de costos',
            subtitle: 'Calcula precios con base de datos',
            icon: <Calculator size={24} />,
            tab: 'materiales',
            color: '#45a7ff',
            onClick: createActionHandler('estimacion-costos', actionHandlers['estimacion-costos'])
        },
        {
            id: 'cotizar-materiales',
            title: 'Pedir una cotización',
            subtitle: 'Solicita cotizaciones personalizadas',
            icon: <DollarSign size={24} />,
            tab: 'materiales',
            color: '#45a7ff',
            onClick: createActionHandler('cotizar-materiales', actionHandlers['cotizar-materiales'])
        },
        {
            id: 'cotizaciones-materiales',
            title: 'Explorar cotizaciones',
            subtitle: 'Ve cotizaciones disponibles',
            icon: <ClipboardList size={24} />,
            tab: 'materiales',
            color: '#45a7ff',
            onClick: createActionHandler('cotizaciones-materiales', actionHandlers['cotizaciones-materiales'])
        },
        {
            id: 'mis-cotizaciones-materiales',
            title: 'Mis cotizaciones de materiales',
            subtitle: 'Revisa tus cotizaciones creadas',
            icon: <FolderKanban size={24} />,
            tab: 'materiales',
            color: '#45a7ff',
            onClick: createActionHandler('mis-cotizaciones-materiales', actionHandlers['mis-cotizaciones-materiales'], true)
        },
        {
            id: 'encontrar-profesionales',
            title: 'Explorar perfiles',
            subtitle: 'Busca profesionales, empresas y más',
            icon: <Users size={24} />,
            tab: 'personas',
            color: '#1a3a7a',
            onClick: createActionHandler('encontrar-profesionales', actionHandlers['encontrar-profesionales'])
        },
         {
            id: 'postularme-ofertas',
            title: 'Postularme a ofertas de trabajo',
            subtitle: 'Aplica a trabajos disponibles',
            icon: <UserPlus size={24} />,
            tab: 'personas',
            color: '#1a3a7a',
            onClick: createActionHandler('postularme-ofertas', actionHandlers['postularme-ofertas'])
        },
        {
            id: 'publicar-oferta-trabajo',
            title: 'Publicar oferta de trabajo',
            subtitle: 'Crea ofertas de empleo',
            icon: <FilePlus size={24} />,
            tab: 'personas',
            color: '#1a3a7a',
            onClick: createActionHandler('publicar-oferta-trabajo', actionHandlers['publicar-oferta-trabajo'])
        },
       
        {
            id: 'mis-postulaciones',
            title: 'Mis postulaciones',
            subtitle: 'Gestiona tus accesos a obras',
            icon: <LockKeyhole size={24} />,
            tab: 'personas',
            color: '#1a3a7a',
            onClick: createActionHandler('mis-postulaciones', actionHandlers['mis-postulaciones'], true)
        },
        {
            id: 'explorar-servicios',
            title: 'Explorar servicios/productos',
            subtitle: 'Descubre servicios y productos',
            icon: <Store size={24} />,
            tab: 'marketplace',
            color: '#7000ff',
            onClick: createActionHandler('explorar-servicios', actionHandlers['explorar-servicios'])
        },
        {
            id: 'ofrecer-servicios',
            title: 'Ofrecer servicios/productos',
            subtitle: 'Publica tus servicios',
            icon: <Package size={24} />,
            tab: 'marketplace',
            color: '#7000ff',
            onClick: createActionHandler('ofrecer-servicios', actionHandlers['ofrecer-servicios'])
        },
        {
            id: 'mis-productos-servicios',
            title: 'Mis productos/servicios',
            subtitle: 'Administra tu portfolio',
            icon: <ShoppingBag size={24} />,
            tab: 'marketplace',
            color: '#7000ff',
            onClick: createActionHandler('mis-productos-servicios', actionHandlers['mis-productos-servicios'], true)
        },
        {
            id: 'comercializar-documentacion',
            title: 'Comercializar documentación',
            subtitle: 'Vende anteproyectos y proyectos',
            icon: <ScrollText size={24} />,
            tab: 'documentacion',
            color: '#059669',
            onClick: createActionHandler('comercializar-documentacion', actionHandlers['comercializar-documentacion'])
        },
        {
            id: 'mis-documentaciones',
            title: 'Mis documentaciones de obra',
            subtitle: 'Gestiona tus documentaciones',
            icon: <Layers size={24} />,
            tab: 'documentacion',
            color: '#059669',
            onClick: createActionHandler('mis-documentaciones', actionHandlers['mis-documentaciones'], true)
        }
    ]

    const adminAction: Action = {
        id: 'backoffice',
        title: 'Backoffice',
        subtitle: 'Panel de administración',
        icon: <MonitorCog size={24} />,
        tab: 'admin',
        color: '#dc2626',
        onClick: createActionHandler('backoffice', actionHandlers['backoffice'])
    }

    const actions: Action[] = isUserAdmin ? [...baseActions, adminAction] : baseActions

    const handleTabChange = (tab: ActionTab) => {
        setSelectedTab(tab)
    }

    const getFilteredActions = () => {
        return actions.filter(action => action.tab === selectedTab)
    }

    const filteredActions = tutorialActive ? getFilteredActions().slice(0, 3) : getFilteredActions()

    return (
        <div className={`${styles.homeActions} ${tutorialActive ? styles.tutorialLocked : ''}`}>
            <ActionTabs
                selected={selectedTab}
                onChange={handleTabChange}
                showAdminTab={isUserAdmin}
                userData={userData}
            />

            <ActionsGrid
                actions={filteredActions}
                disableAnimations={!!tutorialActive}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => {
    return {
        userData: state.general.userData,
        tutorialActive: state.dashboard?.tutorial?.isActive
    }
}

export default connect(mapStateToProps, {
    openFeedTextModal
})(HomeActions)
